import { formatMessage } from 'devextreme/localization';

import PathCheckResultDto from '../../classes/dtos/pathmodule/PathCheckResultDto';
import StringHelper from '../../tools/StringHelper';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';
import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';

/**
 * Single source of truth for the "can this path be saved / published?" checks that every
 * path editor (MetaApi, Reporting, AI, DataTable, Synchro) runs before calling its update API.
 *
 * The five pages used to carry a near-identical `CheckBeforeSaveAndPublish` each; they now build
 * a {@link PathBeforeSaveValidationInput} and delegate here so ordering, severity
 * (`BlockingForSave` vs `BlockingForPublish`) and messages stay identical across modules.
 *
 * Message text comes exclusively from the `PathValidation_*` localization keys.
 */

/** `'none'` = the module has no editable field-name column (Synchro). */
export type FieldNameMode = 'none' | 'free' | 'apiLabel';

/** DevExtreme capability discriminator that means "this transformation needs a parameter". */
const TRANSFORMATION_OPTIONS = 'TransformationOptions';

/**
 * `apiLabel` (MetaApi's TechnicalName) stays short: it's a stable JSON key. `free` (Reporting / AI /
 * DataTable's DisplayName) is a free-text label sourced from arbitrary external field names, so it
 * gets the DB column's full width. Must track `[MaxLength]` on `MetaApiMapping.TechnicalName` /
 * `ReportingMapping.DisplayName` etc. — a mismatch here either blocks names the backend would accept
 * or lets truncation reach SQL Server again.
 */
const FIELD_NAME_MAX_LENGTH: Record<'free' | 'apiLabel', number> = {
    free: 256,
    apiLabel: 64,
};

/**
 * LogicIds of the "constant value" transformations (string / int / bigint). They come back as
 * `TransformationNotConfigurable` capabilities, so they can only be told apart by id — mirrors
 * `TransformationLogic.GetConstantDetails`. Their parameter is `{"Value": "..."}` and must be filled.
 */
const CONSTANT_LOGIC_IDS = [1001, 1002, 1003];

export interface ValidationRow {
    /** Field id handed back to `getFieldLabel` for per-field messages. */
    labelFieldId: number;

    /** Synchro only: source field picked for this target, needed by its capability lookup. */
    sourceFieldId?: number;

    /** `null`/`undefined` means no transformation has been picked yet. */
    transformationLogicId: number | null;
    transformationParameters: string;

    /** DisplayName / TechnicalName. Ignored when `fieldNameMode` is `'none'`. */
    fieldName?: string;

    /** Synchro only: whether this row is flagged as a synchronization key. */
    isKey?: boolean;

    /** Synchro only: the target field is read-only, so it can only be mapped as a synchronization key. */
    targetReadOnly?: boolean;

    /** DataTable only. */
    aggregationGroupBy?: boolean;
    aggregationLogicId?: number | null;
    aggregationParameters?: string | null;
}

export interface PathBeforeSaveValidationInput {
    /** Synchro passes `false` when its detail object failed to load. Defaults to `true`. */
    hasData?: boolean;

    name: string | null | undefined;

    /** The path's `Source`; validated by `instanceof` against the read DTOs. */
    source: unknown;

    /** Synchro only: the path's `Target`, validated with the same rules as `source`. */
    target?: unknown;

    /** Synchro only: require at least one row flagged as a key. */
    requireKey?: boolean;

    /** Rows already filtered down to the exposed / mapped set. */
    selectedRows: ValidationRow[];

    /** Defaults to `'none'` (no field-name checks). */
    fieldNameMode?: FieldNameMode;

    /** DataTable only: aggregation is active as soon as one selected row is a group-by. */
    withAggregation?: boolean;

    transformationsCapabilities: TransformationCapabilitiesDto[];

    /** Allowed transformation LogicIds for a row, or `null` when the source offers none. */
    getAllowedTransformationLogicIds: (row: ValidationRow) => number[] | null;

    /** Human-readable label for a field id, used in per-field messages. */
    getFieldLabel: (labelFieldId: number) => string;
}

function ok(): PathCheckResultDto {
    return new PathCheckResultDto('', PathCheckResultDto.Ok);
}

/** True when `source` points at a real table / path (matches the historical per-page check). */
function isConnectionSelected(source: unknown): boolean {
    if (source instanceof DataSourceDataTableReadDto) {
        return (source.TableReferenceId ?? 0) > 0;
    }
    if (source instanceof PathDataTableReadDto) {
        return (source.PathId ?? 0) > 0;
    }
    return false;
}

function findCapabilities(
    all: TransformationCapabilitiesDto[],
    logicId: number
): TransformationCapabilitiesDto | null {
    return all.find((x) => x.LogicId === logicId) ?? null;
}

/**
 * `BlockingForSave` while a field name is empty / too long / (in `apiLabel` mode) not a valid API
 * label; `null` when the name passes. No-op in `'none'` mode.
 */
export function checkFieldName(value: string | undefined, mode: FieldNameMode): PathCheckResultDto | null {
    if (mode === 'none') {
        return null;
    }

    const name = value ?? '';

    if (name.trim() === '') {
        return new PathCheckResultDto(formatMessage('PathValidation_FieldNameRequired'), PathCheckResultDto.BlockingForSave);
    }

    if (name.length > FIELD_NAME_MAX_LENGTH[mode]) {
        return new PathCheckResultDto(formatMessage('PathValidation_FieldNameTooLong'), PathCheckResultDto.BlockingForSave);
    }

    if (mode === 'apiLabel' && !StringHelper.isValidApiLabel(name)) {
        return new PathCheckResultDto(formatMessage('PathValidation_FieldNameInvalidChars'), PathCheckResultDto.BlockingForSave);
    }

    return null;
}

/** `BlockingForPublish` on the first case-insensitive duplicate among non-empty field names. */
export function checkDuplicateFieldNames(rows: ValidationRow[]): PathCheckResultDto | null {
    const seen = new Set<string>();

    for (const row of rows) {
        const name = (row.fieldName ?? '').trim();
        if (name === '') {
            continue;
        }

        const key = name.toLowerCase();
        if (seen.has(key)) {
            return new PathCheckResultDto(
                formatMessage('PathValidation_DuplicateFieldName', name),
                PathCheckResultDto.BlockingForPublish
            );
        }
        seen.add(key);
    }

    return null;
}

/**
 * Per-field transformation checks, returning the first failure (`BlockingForSave`, except a missing
 * transformation / a missing option parameter, which only block publish) or `null`.
 *
 * "No transformation" is `null`/`undefined` OR the `0` sentinel (Synchro persists an unset
 * transformation as `0`, the other modules as `null`); either way it only matters when "Direct"
 * (LogicId `0`) is not an allowed choice for the row — matching the grid's transformation button.
 * When it does matter it blocks publish only, so an incomplete path can still be saved as a draft.
 */
export function checkRowTransformation(
    row: ValidationRow,
    input: PathBeforeSaveValidationInput
): PathCheckResultDto | null {
    const label = input.getFieldLabel(row.labelFieldId);

    const allowed = input.getAllowedTransformationLogicIds(row);
    if (allowed === null) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_TransformationUnavailable', label),
            PathCheckResultDto.BlockingForSave
        );
    }

    // "No transformation picked": `null`/`undefined`, or the `0` sentinel that Synchro stores for an
    // unset transformation. It only matters when the source can't map straight through, i.e. "Direct"
    // (LogicId 0) is not in the allowed list — same rule as the grid's red/green transformation button.
    // Even then it blocks publish only: a draft save must not be held back by a missing transformation.
    if (row.transformationLogicId == null || row.transformationLogicId === 0) {
        if (allowed.indexOf(0) !== -1) {
            return null;
        }
        return new PathCheckResultDto(
            formatMessage('PathValidation_TransformationMissing', label),
            PathCheckResultDto.BlockingForPublish
        );
    }

    if (allowed.indexOf(row.transformationLogicId) === -1) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_TransformationTypeInvalid', label),
            PathCheckResultDto.BlockingForSave
        );
    }

    const capabilities = findCapabilities(input.transformationsCapabilities, row.transformationLogicId);
    if (capabilities === null) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_TransformationNotFound', label),
            PathCheckResultDto.BlockingForSave
        );
    }

    // Constant transformation (Synchro): its `{"Value": ...}` parameter must not be empty.
    if (CONSTANT_LOGIC_IDS.indexOf(row.transformationLogicId) !== -1) {
        if (!hasConstantValue(row.transformationParameters)) {
            return new PathCheckResultDto(
                formatMessage('PathValidation_TransformationValueMissing', label),
                PathCheckResultDto.BlockingForPublish
            );
        }
        return null;
    }

    if (capabilities.TransformationCapabilitiesType === TRANSFORMATION_OPTIONS && row.transformationParameters === '') {
        return new PathCheckResultDto(
            formatMessage('PathValidation_TransformationSetupMissing', label),
            PathCheckResultDto.BlockingForPublish
        );
    }

    return null;
}

/**
 * Synchro only: a mapped read-only target field is never written by the engine, so it is only useful
 * as a reconciliation key. `BlockingForPublish` when such a row is not flagged as key.
 */
export function checkRowReadOnlyKey(
    row: ValidationRow,
    input: PathBeforeSaveValidationInput
): PathCheckResultDto | null {
    if (row.targetReadOnly && !row.isKey) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_ReadOnlyFieldMustBeKey', input.getFieldLabel(row.labelFieldId)),
            PathCheckResultDto.BlockingForPublish
        );
    }

    return null;
}

/** `true` when `parameters` is `{"Value": "<non-blank>"}`. */
function hasConstantValue(parameters: string): boolean {
    try {
        const value = JSON.parse(parameters || '').Value;
        return value !== undefined && value !== null && String(value).trim() !== '';
    } catch {
        return false;
    }
}

/**
 * DataTable only: when aggregation is active, every non-group-by row must carry a fully configured
 * aggregation. All failures block publish (aggregation is meaningless until then). `null` otherwise.
 */
export function checkRowAggregation(
    row: ValidationRow,
    input: PathBeforeSaveValidationInput
): PathCheckResultDto | null {
    if (!input.withAggregation || row.aggregationGroupBy) {
        return null;
    }

    const label = input.getFieldLabel(row.labelFieldId);

    if (row.aggregationLogicId == null || row.aggregationLogicId === 0) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_AggregationMissing', label),
            PathCheckResultDto.BlockingForPublish
        );
    }

    const capabilities = findCapabilities(input.transformationsCapabilities, row.aggregationLogicId);
    if (capabilities === null) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_AggregationNotFound', label),
            PathCheckResultDto.BlockingForPublish
        );
    }

    if (capabilities.TransformationCapabilitiesType === TRANSFORMATION_OPTIONS && row.aggregationParameters === '') {
        return new PathCheckResultDto(
            formatMessage('PathValidation_AggregationSetupMissing', label),
            PathCheckResultDto.BlockingForPublish
        );
    }

    return null;
}

/**
 * Orchestrates the whole pre-save check. Returns the first blocking issue found, in this order:
 * missing data → name → source → target → at least one field → per-row transformation & aggregation
 * → field names → duplicate field names → synchronization key. Returns `Ok` when nothing blocks.
 */
export function checkPathBeforeSaveAndPublish(input: PathBeforeSaveValidationInput): PathCheckResultDto {
    const fieldNameMode: FieldNameMode = input.fieldNameMode ?? 'none';

    if (input.hasData === false) {
        return new PathCheckResultDto(formatMessage('PathValidation_NoData'), PathCheckResultDto.BlockingForSave);
    }

    if ((input.name ?? '') === '') {
        return new PathCheckResultDto(formatMessage('PathValidation_NameMandatory'), PathCheckResultDto.BlockingForSave);
    }

    if (!isConnectionSelected(input.source)) {
        return new PathCheckResultDto(
            formatMessage('DataSourceSelection_NoConnectionSelected'),
            PathCheckResultDto.BlockingForPublish
        );
    }

    if (input.target !== undefined && !isConnectionSelected(input.target)) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_NoTargetSelected'),
            PathCheckResultDto.BlockingForPublish
        );
    }

    if (input.selectedRows.length === 0) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_NoFieldSelected'),
            PathCheckResultDto.BlockingForPublish
        );
    }

    for (const row of input.selectedRows) {
        const transformationError = checkRowTransformation(row, input);
        if (transformationError !== null) {
            return transformationError;
        }

        const aggregationError = checkRowAggregation(row, input);
        if (aggregationError !== null) {
            return aggregationError;
        }
    }

    if (fieldNameMode !== 'none') {
        for (const row of input.selectedRows) {
            const fieldNameError = checkFieldName(row.fieldName, fieldNameMode);
            if (fieldNameError !== null) {
                return fieldNameError;
            }
        }

        const duplicateError = checkDuplicateFieldNames(input.selectedRows);
        if (duplicateError !== null) {
            return duplicateError;
        }
    }

    for (const row of input.selectedRows) {
        const readOnlyKeyError = checkRowReadOnlyKey(row, input);
        if (readOnlyKeyError !== null) {
            return readOnlyKeyError;
        }
    }

    if (input.requireKey && !input.selectedRows.some((row) => row.isKey)) {
        return new PathCheckResultDto(
            formatMessage('PathValidation_NoKeySelected'),
            PathCheckResultDto.BlockingForPublish
        );
    }

    return ok();
}

export type PathChecklistStatus = 'ok' | 'blocksSave' | 'blocksPublish';

export interface PathChecklistItem {
    /** Stable id; also the i18n label suffix (`PathChecklist_<Id>`). */
    id: 'name' | 'source' | 'target' | 'fieldSelected' | 'transformations' | 'aggregations' | 'fieldNames' | 'duplicates' | 'readOnlyKey' | 'key';
    label: string;
    status: PathChecklistStatus;
    /** One line per offending row, when the item is not ok (empty / absent when ok). */
    details?: string[];
}

function statusOf(result: PathCheckResultDto | null): PathChecklistStatus {
    if (result === null) {
        return 'ok';
    }
    return result.Level === PathCheckResultDto.BlockingForSave ? 'blocksSave' : 'blocksPublish';
}

/** Runs `check` on every row and keeps every failure (one per offending row). */
function allRowIssues(
    rows: ValidationRow[],
    check: (row: ValidationRow) => PathCheckResultDto | null
): PathCheckResultDto[] {
    const issues: PathCheckResultDto[] = [];
    for (const row of rows) {
        const issue = check(row);
        if (issue !== null) {
            issues.push(issue);
        }
    }
    return issues;
}

/** `blocksSave` if any issue blocks saving, else `blocksPublish` if any, else `ok`. */
function statusOfMany(issues: PathCheckResultDto[]): PathChecklistStatus {
    if (issues.length === 0) {
        return 'ok';
    }
    return issues.some((i) => i.Level === PathCheckResultDto.BlockingForSave) ? 'blocksSave' : 'blocksPublish';
}

/**
 * The same checks as {@link checkPathBeforeSaveAndPublish}, but reported item by item instead of
 * stopping at the first failure — for the "publish readiness" checklist on the Checking tab.
 * Reuses every sub-check, so it can never drift from the pre-save gate. Empty when `hasData` is false.
 */
export function getPathPublishChecklist(input: PathBeforeSaveValidationInput): PathChecklistItem[] {
    if (input.hasData === false) {
        return [];
    }

    const fieldNameMode: FieldNameMode = input.fieldNameMode ?? 'none';
    const items: PathChecklistItem[] = [];

    items.push({
        id: 'name',
        label: formatMessage('PathChecklist_Name'),
        status: (input.name ?? '') !== '' ? 'ok' : 'blocksSave',
    });

    items.push({
        id: 'source',
        label: formatMessage('PathChecklist_Source'),
        status: isConnectionSelected(input.source) ? 'ok' : 'blocksPublish',
    });

    if (input.target !== undefined) {
        items.push({
            id: 'target',
            label: formatMessage('PathChecklist_Target'),
            status: isConnectionSelected(input.target) ? 'ok' : 'blocksPublish',
        });
    }

    const hasFields = input.selectedRows.length > 0;
    items.push({
        id: 'fieldSelected',
        label: formatMessage('PathChecklist_FieldSelected'),
        status: hasFields ? 'ok' : 'blocksPublish',
    });

    if (hasFields) {
        const transformationIssues = allRowIssues(input.selectedRows, (row) => checkRowTransformation(row, input));
        items.push({
            id: 'transformations',
            label: formatMessage('PathChecklist_Transformations'),
            status: statusOfMany(transformationIssues),
            details: transformationIssues.map((i) => i.Message),
        });

        if (input.withAggregation) {
            const aggregationIssues = allRowIssues(input.selectedRows, (row) => checkRowAggregation(row, input));
            items.push({
                id: 'aggregations',
                label: formatMessage('PathChecklist_Aggregations'),
                status: statusOfMany(aggregationIssues),
                details: aggregationIssues.map((i) => i.Message),
            });
        }

        if (fieldNameMode !== 'none') {
            const fieldNameIssues = allRowIssues(input.selectedRows, (row) => checkFieldName(row.fieldName, fieldNameMode));
            items.push({
                id: 'fieldNames',
                label: formatMessage('PathChecklist_FieldNames'),
                status: statusOfMany(fieldNameIssues),
                details: fieldNameIssues.map((i) => i.Message),
            });

            const duplicateIssue = checkDuplicateFieldNames(input.selectedRows);
            items.push({
                id: 'duplicates',
                label: formatMessage('PathChecklist_Duplicates'),
                status: statusOf(duplicateIssue),
                details: duplicateIssue ? [duplicateIssue.Message] : [],
            });
        }

        if (input.selectedRows.some((row) => row.targetReadOnly)) {
            const readOnlyKeyIssues = allRowIssues(input.selectedRows, (row) => checkRowReadOnlyKey(row, input));
            items.push({
                id: 'readOnlyKey',
                label: formatMessage('PathChecklist_ReadOnlyKey'),
                status: statusOfMany(readOnlyKeyIssues),
                details: readOnlyKeyIssues.map((i) => i.Message),
            });
        }

        if (input.requireKey) {
            items.push({
                id: 'key',
                label: formatMessage('PathChecklist_Key'),
                status: input.selectedRows.some((row) => row.isKey) ? 'ok' : 'blocksPublish',
            });
        }
    }

    return items;
}
